import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationDialogComponent, MessageDialogComponent, NoticeService, PageComponent } from '@tytapp/common';
import { environment } from '@tytapp/environment';
import { isClientSide } from '@tytapp/environment-utils';
import { HostApi } from '@tytapp/common';
import { UserService } from '@tytapp/user';
import { ApiGiftItem, ApiGiftOrder, ApiMembership, ApiPaymentSource, ApiUser, GiftsApi, UsersApi } from '@tytapp/api';
import { CartService } from '../cart.service';
import { TEST_SCENARIOS, TestScenario } from '../membership-test-scenarios';
import { MEMBERSHIP_PROPERTY_HELPER, isExpiring, isPastRenewal } from '../membership-properties';

@Component({
    selector: 'tyt-membership-accounts',
    templateUrl: './membership-account.component.html',
    styleUrls: ['./membership-account.component.scss'],
})
export class MembershipAccountComponent extends PageComponent {
    private userService = inject(UserService);
    private notices = inject(NoticeService);
    private userApi = inject(UsersApi);
    private giftsApi = inject(GiftsApi);
    private cartService = inject(CartService);
    private hostApi = inject(HostApi);

    readonly MembershipProperties = MEMBERSHIP_PROPERTY_HELPER;

    get isExpiring() {
        return isExpiring(this.membership);
    }

    get accountsUrl() { return environment.urls.accounts; }

    manageDisplay: string = 'account';
    updateSuccessMessage: string = '';
    updateErrorMessage: string = '';
    editEmail: boolean = false;
    editPassword: boolean = false;
    user: ApiUser;
    membership: ApiMembership;
    giftOrders: ApiGiftOrder[];
    giftItems: ApiGiftItem[] = [];
    sponsorshipItems: ApiGiftItem[] = [];
    paymentSource: ApiPaymentSource;
    email: string;
    currentPassword: string;
    newPassword: string;
    cartStatus: string = '';
    showHelpNote: boolean = false;

    get shouldShowUpdateBillingOption() {
        if (!this.membership)
            return false;

        if (this.membership.cancelled_at)
            return false;

        if (!this.membership.status)
            return false;

        if (this.membership.status.canceled)
            return false;

        if (this.membership.status.cancel_at_period_end)
            return false;

        if (['expired', 'not_paid'].includes(this.membership.payment_status))
            return false;

        return true;
    }

    get isWebMembership() {
        return !this.isMobileMembership;
    }

    get isMobileMembership() {
        if (!this.membership)
            return false;
        return ['iap_apple', 'google_play'].includes(this.membership.gateway_name);
    }

    get giftBillingEnabled() {
        return this.hostApi.capabilities.includes('web_billing:gift')
            || this.hostApi.capabilities.includes('platform_billing:gift');
    }

    get platformCancelEnabled() {
        return this.hostApi.capabilities.includes(`platform_billing:in_app_cancellation`);
    }

    get membershipBillingEnabled() {
        return this.hostApi.capabilities.includes('web_billing:membership')
            || this.hostApi.capabilities.includes('platform_billing:membership');
    }
    get membershipWebBillingEnabled() {
        return this.hostApi.capabilities.includes('web_billing:membership');
    }

    /**
     * Used to show a message to the user if the system respects their entitlement but the membership returned by
     * user.membership indicates the membership itself is not entitled.
     */
    get entitledButNotEntitled() {
        if (!this.user)
            return false;

        if (this.user.entitled && this.user.membership && !this.user.membership.entitled)
            return true;
        return false;
    }

    get shouldShowCancelOption() {
        if (!this.membership)
            return false;
        if (!this.membership.status)
            return false;
        if (this.membership.cancelled_at)
            return false;
        if (this.membership.status.canceled)
            return false;
        if (['expired', 'not_paid'].includes(this.membership.payment_status))
            return false;
        if (this.membership.gateway_name === 'free' && this.membership.contribution)
            return false;

        return true;
    }

    get eligibleForReactivateMembership() {

        // Must have membership status so we can determine eligibility

        if (!this.user || !this.user.membership || !this.user.membership.status)
            return false;

        // Must be in cancellation grace period
        if (!this.user.membership.status.cancel_at_period_end || this.user.membership.status.canceled)
            return false;

        // Must not have been refunded
        if (this.user.membership.refunded_at)
            return false;

        // Only currently supported on Stripe gateway
        if (this.user.membership.gateway_name == 'stripe')
            return true;

        return false;
    }

    get eligibleForChangeMembership() {
        // Must be logged in, and user must have a membership
        if (!this.user || !this.user.membership)
            return false;

        // Must have membership status to determine eligibility
        if (!this.user.membership.status)
            return false;

        // Must not have cancelled membership
        if (this.user.membership.cancelled_at || this.user.membership.status.canceled || this.user.membership.status.cancel_at_period_end)
            return false;

        // Not available for free memberships
        if (this.user.membership.gateway_name === 'free')
            return false;

        // Not available for memberships that are past due
        if (this.user.membership.payment_status === 'past_due')
            return false;

        return true;
    }

    get pageComponentId() {
        return 'membershipAccount';
    }

    showPaypalCancelPage = false;
    showIapCancelPage = false;

    private reactivateOnLoad = false;

    get displayableMembershipStatus() {
        if (!this.membership)
            return 'N/A';

        if (this.membership.contribution) {
            return 'Time Limited';
        }

        switch (this.membership.payment_status) {
            case "good_standing":
                return "Current";
            case "gifted":
                if (this.membership.gift_item.sponsorship) {
                    return "Sponsored Membership";
                }
                return "Gift Membership";
            case "expired_gift":
                if (this.membership.gift_item.sponsorship) {
                    return "Sponsored Membership (Expired)";
                }
                return "Gift Membership (Expired)";
            case "canceled":
                return "Canceled";
            case "canceling":
                return "Canceled";
            case "not_paid":
                return "Expired";
            case "past_due":
                return "Payment past due";
            case "expiring":
                return "Expiring";
            case "expired":
                return "Expired";
            default:
                return this.membership.payment_status;
        }
    }

    get shouldShowRenewal(): boolean {
        if (!this.membership)
            return false;
        if (this.membership.cancelled_at)
            return false;
        if (!this.membership.active)
            return false;
        if (['expired', 'expiring', 'not_paid'].includes(this.membership.payment_status))
            return false;

        return true;
    }

    get shouldShowPastDueNotice(): boolean {
        if (this.membership && this.membership.payment_status === 'past_due')
            return true;
        return false;
    }

    get isPastRenewal(): boolean {
        return isPastRenewal(this.membership);
    }

    get membershipPaymentStatus() {
        if (!this.membership)
            return undefined;

        if (this.membership.contribution)
            return undefined;

        return this.membership.payment_status;
    }

    get displayableMembershipStatusColor() {
        if (!this.membership)
            return '#fff';

        switch (this.membership.payment_status) {
            case "good_standing":
                return "#fff";
            case "gifted":
                return "#fff";
            case "expired_gift":
                return "#A00000";
            case "canceled":
                return "#A00000";
            case "canceling":
                return "#A00000";
            case "not_paid":
                return "#A00000";
            case "past_due":
                return "#A00000";
            case "expiring":
                return "#A00000";
            case "expired":
                return "#A00000";
            default:
                return '#fff';
        }
    }

    displayableGiftStatusColor(giftItem: ApiGiftItem) {
        if (!giftItem)
            return '#fff';

        if (giftItem.renewable_expired)
            return "#A00000";
        if (this.giftItemPastDue(giftItem))
            return "#A00000";

        return '#fff';
    }

    private _overriddenMembershipType: string;

    set membershipType(value) {
        this._overriddenMembershipType = value;
    }

    get membershipType() {
        if (this._overriddenMembershipType)
            return this._overriddenMembershipType;

        if (!this.membership) {
            if (this.isStaff)
                return 'staff';

            return null;
        }

        if (!this.membership.status)
            return 'missing_gms';

        if (this.membership.complimentary)
            return 'complimentary';
        if (this.membership.gift_item)
            return 'gift';
        if (this.membership.contribution)
            return 'donation';

        return 'standard';
    }

    dbgRenewInPast() {
        this.membership.renews_on = new Date(new Date().getTime() - 60 * 60 * 24 * 5 * 1000).toString();
    }

    dbgRenewInFuture() {
        this.membership.renews_on = new Date(new Date().getTime() + 60 * 60 * 24 * 5 * 1000).toString();
    }

    get debugMode() {
        if (isClientSide() && window.localStorage?.__tyt_acctdbg)
            return true;

        return environment.showDevTools;
    }

    showDebugger = false;
    showScenarios = false;

    ready: boolean = false;

    get isStaff() {
        return this.user?.staff ?? false;
    }

    async init() {
        await this.cartService.ready;
        await this.userService.ready;

        this.subscribe(this.route.queryParams, queryParams => {
            this.reactivateOnLoad = !!queryParams.reactivate;
        });

        this.subscribe(this.userService.identityChanged, async user => {
            if (this.testScenario)
                return;

            if (!user) {
                this.userService.requireLogin();
                return;
            }

            if (user.type === 'Guest') {
                this.membership = user.membership;
                this.user = null;
                this.ready = true;
                return;
            }

            this.user = user;

            /**
             * Refresh the user right away if it was fetched too long ago.
             */
            if (Date.now() - this.userService.userUpdatedAt > 1000 * 60) {
                try {
                    let u = await <any>this.userApi.getCurrentUser().toPromise();

                    if (this.testScenario)
                        return;

                    this.user = u;
                    if (isClientSide()) {
                        this.logger.info("Fresh user: ");
                        this.logger.inspect(this.user);
                    }
                } catch (e) {
                    if (e.json)
                        e = e.json();

                    this.logger.error(`Error while fetching current user: ${e.stack ?? e}`);
                    this.user = null;
                }
            }

            if (!this.user) {
                this.userService.goToLogin();
                return;
            }

            if (this.user.rstatus) {
                this.redirection.go('/account/missing-payment', true);
                return;
            }

            if (!this.giftOrders) {
                try {
                    this.giftOrders = await this.giftsApi.getGiftOrders().toPromise();
                    let list = this.giftOrders.map((order) => {
                        return order.gift_items;
                    });

                    let unfilteredList: ApiGiftItem[] = [].concat.apply([], list);

                    this.giftItems = unfilteredList.slice().filter((item) => {
                        return !item.sponsorship;
                    });

                    this.sponsorshipItems = unfilteredList.slice().filter((item) => {
                        return item.sponsorship;
                    });

                } catch (e) {
                    this.logger.error(`Error occurred while fetching gift orders: ${e.stack ?? e}`);
                }
            }

            if (this.user && this.user.membership) {
                this.membership = this.user.membership;
            } else {
                this.membership = null;
            }

            this.showPaypalCancelPage =
                this.membership
                && ['paypal_subscription', 'paypal_agreement'].indexOf(this.membership.gateway_name) >= 0
                ;

            this.showIapCancelPage = this.membership != null && this.membership.gateway_name === 'iap_apple';
            this.ready = true;

            if (this.reactivateOnLoad)
                this.reactivateMembership();
        });

        if (!this.paymentSource) {
            try {
                this.paymentSource = await this.userApi.getPaymentSource().toPromise();
            } catch (e) {
                this.logger.error(`Failed to retrieve payment source:`);
                this.logger.error(e);
            }
        }

        this.updateCartStatus();

        if (this.debugMode) {
            this.showScenarios = true;
        }
    }

    get giftBenefits() {
        if (this.membership.gift_item.product_sku.includes('insider')) {
            return 'insider';
        }
        if (this.membership.gift_item.product_sku.includes('activist')) {
            return 'activist';
        }
        return '';
    }

    reactivateMembership() {
        this.notices.dismissNotices();

        if (!this.membership || !this.membership.status || !this.membership.status.cancel_at_period_end)
            return;

        // check if thye are IAP and

        if (this.membership.gateway_name === 'iap_apple') {
            this.shell.showDialog(MessageDialogComponent, 'Go to the App Store', 'To reactivate your TYT membership, visit the Subscriptions section of your App Store account on your iOS device.');
            return;
        }

        if (this.membership.gateway_name === 'google_play') {
            this.shell.showDialog(MessageDialogComponent, 'Go to the Play Store', 'To reactivate your TYT membership, visit the Subscriptions section of the Play Store on your Android device.');
            return;
        }

        this.shell.showDialog(ConfirmationDialogComponent, 'Are you sure you want to reactivate your TYT membership? You won\'t be charged until your regular renewal date.', async confirmed => {
            if (confirmed) {
                try {
                    await this.userApi.reactivateMembership().toPromise();
                    this.user = <any>await this.userApi.getCurrentUser().toPromise();
                    this.shell.showDialog(MessageDialogComponent, 'Welcome Back!', 'Your TYT membership has been reactivated.');
                    this.redirection.go('/settings/memberships');
                } catch (e) {
                    if (e.json)
                        e = e.json();

                    alert(e.error || e.message || 'An error occurred. Please try again or contact Member Services at support@tytnetwork.com');
                }
            }
        });
    }

    capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    onCartClick() {
        if (this.cartService.currentCartStatus === 'gift') {
            this.router.navigate(['/gift']);
        } else if (this.cartService.currentCartStatus === 'sponsorship') {
            this.router.navigate(['/sponsorship']);
        }
    }

    updateCartStatus() {
        let cartLength = this.cartService.cartLength;
        if (cartLength) {
            this.cartStatus = cartLength.toString();
        } else {
            this.cartStatus = 'Your cart is empty';
        }
    }

    giftItemName(gift: ApiGiftItem) {
        let split = gift.product_sku.split('_');
        let duration = gift.months ? `${gift.months} months` : this.capitalizeFirstLetter(split[1]);
        return `TYT ${this.capitalizeFirstLetter(split[0])} Membership (${duration})`;
    }

    giftRenewingStatus(gift: ApiGiftItem) {
        if (gift.recurring) {
            let period = gift.product_sku.split('_')[1];
            return `Renews ${period}`;
        }
        return 'Not Renewing';
    }

    giftPrice(gift: ApiGiftItem) {
        if (gift.recurring) {
            return gift.dollar_price;
        }

        let total = +gift.dollar_price * gift.months;
        return total;
    }

    getViewSponsorshipRedemptionUrl(gift: ApiGiftItem, number: number) {
      return `${environment.urls.accounts}/sponsorship-view/${gift.code}/${number}`;
    }


    get periods() {
        if (!this.membership?.product)
            return '';

        return this.membership.product.period === 'monthly' ? 'months' : 'years';
    }

    get perPeriod() {
        if (!this.membership?.product)
            return '';

        return `/${this.membership.product.period === 'monthly' ? 'month' : 'year'}`;
    }

    async cancelGiftMembership(gift: ApiGiftItem) {
        let item = gift.sponsorship ? 'package' : 'gift membership';
        let title = gift.sponsorship ? 'Cancel this package - are you sure?' : '';
        let message = gift.sponsorship ? `Clicking “Cancel Package” will send cancellation notices to ${gift.redemptions_count} users who are enjoying a TYT Insider monthly renewing membership because of your generosity in funding this Gift Membership Package.  Cancellations cannot be undone.` : `Are you sure you want to cancel this gift membership? ${this.capitalizeFirstLetter(gift.recipient_name)} will no longer have access to premium content.`;
        this.shell.showDialog(ConfirmationDialogComponent, message, async confirmed => {
            if (confirmed) {
                let cancelResponse: ApiGiftItem;
                this.ready = false;

                try {
                    cancelResponse = await this.giftsApi.cancelGiftItem(gift.code).toPromise();
                    this.giftOrders = await this.giftsApi.getGiftOrders().toPromise();
                    // let list = this.giftOrders.map((order) => {
                    //   return order.gift_items;
                    // });
                    // this.giftItems = [].concat.apply([], list);
                    let list = this.giftOrders.map((order) => {
                        return order.gift_items;
                    });

                    let unfilteredList: ApiGiftItem[] = [].concat.apply([], list);

                    this.giftItems = unfilteredList.slice().filter((item) => {
                        return !item.sponsorship;
                    });

                    this.sponsorshipItems = unfilteredList.slice().filter((item) => {
                        return item.sponsorship;
                    });


                    this.shell.showDialog(MessageDialogComponent, 'Success', `The selected ${item} has been cancelled.`);
                } catch (e) {
                    this.logger.error(`Error occurred while cancelling recurring gift: ${e.stack ?? e}`);
                    this.shell.showDialog(MessageDialogComponent, 'Uh oh!', `An error occurred while cancelling your ${item}. If the issue persists, please contact support@tytnetwork.com`);
                }

                this.ready = true;
            }
        }, { title });

    }

    async sendReminder(gift: ApiGiftItem) {
        try {
            let response = await this.giftsApi.sendGiftEmailReminder(gift.code).toPromise();

            // handle UI to let user know if its a success or if it didn't send
            alert(`A reminder email has been sent!`);
        } catch (e) {
            if (e.json)
                e = e.json();

            alert(e.message || e.error || `An error occurred, please try again later or contact support@tytnetwork.com if it persists`);
        }
    }

    giftItemPastDue(item: ApiGiftItem) {
        return new Date(item.renews_on).getTime() + 1000 * 60 * 60 * 24 < Date.now();
    }

    get renewalChargePastDue() {
        if (this.paymentSource) {
            return this.paymentSource.status[0] === 'past_due' ? 'past_due' : null;
        } else {
            return null;
        }
    }

    get isRegistered() {
        return this.user !== null;
    }

    applyTestScenario(scenario: TestScenario) {
        if (!scenario)
            return;

        setTimeout(() => {
            this.testScenario = scenario;
            this.membership = <ApiMembership>scenario.membership;
            if (scenario.user)
                this.user = scenario.user;
        });
    }

    testScenarios: TestScenario[] = TEST_SCENARIOS;

    private _testScenarioId: number = -1;

    get testScenarioId() { return this._testScenarioId; }

    testScenario: TestScenario;

    set testScenarioId(value) {
        this._testScenarioId = value;
        setTimeout(() => {
            if (value < 0) {
                this.ready = false;
                window.location.reload();
                return;
            }
            this.applyTestScenario(this.testScenarios[this._testScenarioId]);
        });
    }

    nextScenario() {
        if (this.testScenario) {
            let index = this.testScenarios.indexOf(this.testScenario);
            if (index + 1 >= this.testScenarios.length) {
                this.testScenarioId = 0;
            } else {
                this.testScenarioId += 1;
            }
        } else {
            this.testScenarioId = 0;
        }
    }

    previousScenario() {
        if (this.testScenario) {
            let index = this.testScenarios.indexOf(this.testScenario);
            if (index === 0) {
                this.testScenarioId = this.testScenarios.length - 1;
            } else {
                this.testScenarioId = index - 1;
            }
        }
    }

    capturedScenario: string;

    captureScenario() {
        let label = prompt(`Choose a descriptive name for this scenario (eg 'The user has a gift membership which is refunded')`);
        let id = prompt(`Choose an ID for this scenario (all lowercase, with dashes, eg 'gift-refunded')`);
        let description = prompt(`Enter a longer description for this scenario`);

        this.capturedScenario = JSON.stringify({
            id,
            label,
            description,
            membership: this.membership
        }, undefined, 2);
    }

    get billingUpdateUrl() {
        return `${ environment.urls.accounts }/billing/update`;
    }

    get membershipChangeUrl() {
        return `${ environment.urls.accounts }/membership/change`;
    }

    get whereToManageMembership() {
        if (this.membership?.gateway_name === 'google_play')
            return `google_play`;
        if (this.membership?.gateway_name === 'iap_apple')
            return `iap_apple`;
        if (this.membership?.gateway_name === 'actblue')
            return `actblue`;
        if (this.membership?.gateway_name === 'youtube')
            return `youtube`;
        if (!this.membershipWebBillingEnabled)
            return 'tyt_com';

        return `here`;
    }
}
